import { useFormik } from 'formik';
import { useAppDispatch } from '@store/hooks';
import {
  addFACategories,
  getFACategoriesList
} from '@store/Finance/FixedAssets/FACategories/FACategoriesSlice';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import { DataIcons } from '@assets/icons';
import useGetFACategoriesList from '@hooks/FixedAssets/useGetFACategoriesList';
import useGetAccounts from '@hooks/Finance/ChartAccountsDetails/useGetAccounts';
import useGetDepreciationMethod from '@hooks/Lookups/use-Get_DEPRECIATION_METHOD';
import { useEffect } from 'react';
import useGetCategoriesList from '@hooks/INV/useGetCategoriesList';

interface Props {
  onClose: () => void;
}

const AddFaCategory = ({ onClose }: Props) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const { FACategoriesList, loading, error } = useGetFACategoriesList();
  const { OptionsAccounts } = useGetAccounts();

  const { CategoriesOptions } = useGetCategoriesList();

  const { DEPRECIATION_METHOD } = useGetDepreciationMethod();

  //#region Formik
  const formik = useFormik({
    initialValues: {
      name: '',
      name2: '',
      categoryId: '',
      description: '',
      icon: '',
      lifeInMonths: 0, // يجب ان لا يكون أكبر من 100
      depreciationMethod: '',
      percentage: 0,
      invCategoryId: '',
      assetCostAccount: '', // حساب التكلفة المرتبط بهذه الفئة في دليل الحسابات (COA - Chart of Accounts)
      deprnExpenseAccount: '', //حساب مصروف الإهلاك في دليل الحسابات، حيث يتم تسجيل الإهلاك الدوري للأصول.
      deprnReserveAccount: '', // حساب احتياطي الإهلاك، وهو الحساب المستخدم لحجز مبالغ الإهلاك المحتملة لتغطية استبدال الأصول مستقبلاً
      accumulatedDeprnAccount: '', // حساب مجمع الإهلاك، وهو الحساب الذي يتم تسجيل جميع الإهلاكات المتراكمة فيه.
      assetClearingAccount: '' // حساب تسوية الأصول، ويستخدم عادة عند شراء أصول جديدة قبل تسجيلها في الحسابات النهائية.
    },

    enableReinitialize: true,
    // validationSchema: AddApPaymentsSchema(t),

    onSubmit: values => {
      dispatch(
        addFACategories({
          name: values.name,
          name2: values.name2,
          categoryId: values.categoryId || null,
          description: values.description,
          icon: values.icon,
          lifeInMonths: values.lifeInMonths,
          depreciationMethod: values.depreciationMethod,
          percentage: values.percentage,
          assetCostAccount: values.assetCostAccount || null,
          deprnExpenseAccount: values.deprnExpenseAccount || null,
          deprnReserveAccount: values.deprnReserveAccount || null,
          invCategoryId: values.invCategoryId || null,
          accumulatedDeprnAccount: values.accumulatedDeprnAccount || null,
          assetClearingAccount: values.assetClearingAccount || null
        })
      )
        .unwrap()
        .then(res => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Add' });
            dispatch(getFACategoriesList());
            formik.resetForm();
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });
  const { values, handleChange, errors, touched, setFieldValue } = formik;
  //#endregion

  // console.log(values.depreciationMethod);

  // useEffect(() => {
  //   if (values.depreciationMethod === 'StraightLine' && Number(values.lifeInMonths) > 0) {
  //     const percentage = parseFloat((1 / Number(values.lifeInMonths)).toFixed(5));

  //     if (values.percentage !== percentage) {
  //       setFieldValue('percentage', percentage);
  //     }
  //   }
  // }, [values.depreciationMethod, values.lifeInMonths]);

  useEffect(() => {
    if (values.depreciationMethod === 'SL' && Number(values.lifeInMonths) > 0) {
      const monthlyPercentage = parseFloat((100 / Number(values.lifeInMonths)).toFixed(4)); // نسبة شهرية بدقة 4 خانات عشرية

      if (values.percentage !== monthlyPercentage) {
        setFieldValue('percentage', monthlyPercentage);
      }
    }
  }, [values.depreciationMethod, values.lifeInMonths]);

  return (
    <>
      <Form onSubmit={formik.handleSubmit}>
        <div className='row'>
          {/* LocalName */}
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('LocalName')}</label>
              <Form.Control
                className='form-control '
                type='text'
                autoComplete='off'
                name='name2'
                onChange={handleChange}
                value={values.name2}
                isInvalid={!!(touched.name2 && errors.name2)}
              />
              <Form.Control.Feedback type='invalid'>{errors.name2}</Form.Control.Feedback>
            </div>
          </div>
          {/* GlobalName */}
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('GlobalName')}</label>
              <Form.Control
                className='form-control '
                type='text'
                autoComplete='off'
                name='name'
                onChange={handleChange}
                value={values.name}
                isInvalid={!!(touched.name && errors.name)}
              />
              <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
            </div>
          </div>
          {/* categoryId */}
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'> {t('تابع لـ')}</label>
              <Form.Select
                className='form-select'
                name='categoryId'
                onChange={handleChange}
                value={values.categoryId}
                isInvalid={!!(touched.categoryId && errors.categoryId)}
                // disabled
              >
                <option value={''}>{i18n.language === 'en' ? 'فئة رئيسية' : 'فئة رئيسية'}</option>
                {FACategoriesList &&
                  FACategoriesList?.map((item, idx) => (
                    <option key={++idx} value={item.id}>
                      {i18n.language === 'en' ? item.name : item.name2}
                    </option>
                  ))}
              </Form.Select>
            </div>
          </div>
          {/* invCategoryId */}
          <div className='col-lg-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('Inv Category')}</label>
              <Select
                classNamePrefix='react-select'
                // className="text-center"
                isLoading={loading}
                isSearchable={true}
                isClearable
                options={CategoriesOptions}
                value={CategoriesOptions?.find(f => f.value === values.invCategoryId)}
                onChange={option => setFieldValue('invCategoryId', option?.value)}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              />
            </div>
          </div>
          {/* Icon */}
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('Icon')}</label>

              <Select
                classNamePrefix='react-select'
                isSearchable={true}
                options={DataIcons}
                onChange={option => setFieldValue('icon', option?.value)}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              />
              <>{errors.icon}</>
            </div>
          </div>
          {/* 'طريقة الإهلاك' */}
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('طريقة الإهلاك')}</label>
              <Form.Select
                className='form-select'
                name='depreciationMethod'
                onChange={handleChange}
                value={values.depreciationMethod}
                // isInvalid={touched.depreciationMethod && errors.depreciationMethod}
              >
                <option key={null}>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                {DEPRECIATION_METHOD?.map((item: any, idx: number) =>
                  item.list?.map((list: any) => (
                    <option key={++idx} value={list.lookupCode}>
                      {i18n.language === 'ar' ? list.meaning2 : list.meaning}
                    </option>
                  ))
                )}
              </Form.Select>
            </div>
          </div>
          {/* lifeInMonths */}
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('العمر بالشهور')}</label>
              <Form.Control
                className='form-control '
                type='text'
                autoComplete='off'
                name='lifeInMonths'
                onChange={handleChange}
                value={values.lifeInMonths}
                isInvalid={!!(touched.lifeInMonths && errors.lifeInMonths)}
                onInput={e => {
                  const input = e.target as HTMLInputElement;
                  input.value = input.value?.replace(/[^0-9]/g, '');
                }}
              />
            </div>
          </div>
          {/* 'نسبة الإهلاك' */}
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>
                {values.depreciationMethod === 'SL'
                  ? 'نسبة الإهلاك الشهرية'
                  : 'النسبة السنوية للإهلاك'}
              </label>
              <div className='input-group flex-nowrap'>
                <Form.Control
                  disabled={values.depreciationMethod === 'SL'}
                  className='form-control text-center'
                  type='text'
                  autoComplete='off'
                  name='percentage'
                  onChange={handleChange}
                  value={values.percentage}
                  isInvalid={!!(touched.percentage && errors.percentage)}
                  onInput={e => {
                    const input = e.target as HTMLInputElement;
                    input.value = input.value?.replace(/[^0-9]/g, '');
                  }}
                />
                <span className='input-group-text text-theme '>%</span>
              </div>
              <div className='text-danger small'>{touched.percentage && errors.percentage}</div>
            </div>
          </div>
          {/* 'حساب تكلفة الأصول' */}
          <div className='col-lg-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('حساب تكلفة الأصول')}</label>
              <Select
                classNamePrefix='react-select'
                // className="text-center"
                isLoading={loading}
                isSearchable={true}
                isClearable
                options={OptionsAccounts}
                value={OptionsAccounts?.find(f => f.value === values.assetCostAccount)}
                onChange={option => setFieldValue('assetCostAccount', option?.value)}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              />
            </div>
          </div>
          {/* 'حساب مصاريف الإهلاك' */}
          <div className='col-lg-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('حساب مصاريف الإهلاك')}</label>
              <Select
                classNamePrefix='react-select'
                // className="text-center"
                isLoading={loading}
                isSearchable={true}
                isClearable
                options={OptionsAccounts}
                value={OptionsAccounts?.find(f => f.value === values.deprnExpenseAccount)}
                onChange={option => setFieldValue('deprnExpenseAccount', option?.value)}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              />
            </div>
          </div>
          {/* 'حساب احتياطي الإهلاك' */}
          <div className='col-lg-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('حساب احتياطي الإهلاك')}</label>
              <Select
                classNamePrefix='react-select'
                // className="text-center"
                isLoading={loading}
                isSearchable={true}
                isClearable
                options={OptionsAccounts}
                value={OptionsAccounts?.find(f => f.value === values.deprnReserveAccount)}
                onChange={option => setFieldValue('deprnReserveAccount', option?.value)}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              />
            </div>
          </div>
          {/* 'حساب مجمع الإهلاك' */}
          <div className='col-lg-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('حساب مجمع الإهلاك')}</label>
              <Select
                classNamePrefix='react-select'
                // className="text-center"
                isLoading={loading}
                isSearchable={true}
                isClearable
                options={OptionsAccounts}
                value={OptionsAccounts?.find(f => f.value === values.accumulatedDeprnAccount)}
                onChange={option => setFieldValue('accumulatedDeprnAccount', option?.value)}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              />
            </div>
          </div>

          {/* 'حساب تسوية الأصـول' */}
          <div className='col-lg-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('حساب تسوية الأصول')}</label>
              <Select
                classNamePrefix='react-select'
                // className="text-center"
                isLoading={loading}
                isSearchable={true}
                isClearable
                options={OptionsAccounts}
                value={OptionsAccounts?.find(f => f.value === values.assetClearingAccount)}
                onChange={option => setFieldValue('assetClearingAccount', option?.value)}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              />
            </div>
          </div>

          {/* Note */}
          <div className='col-xl-8'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('Note')}</label>
              <Form.Control
                className='form-control '
                type='text'
                autoComplete='off'
                name='description'
                onChange={handleChange}
                value={values.description}
                isInvalid={!!(touched.description && errors.description)}
              />
            </div>
          </div>
        </div>

        <div className='text-center'>
          <div className='d-flex justify-content-between gap-2'>
            <button
              disabled={formik.isSubmitting}
              type='submit'
              className='btn btn-lg btn-theme mb-1 w-100 rounded-1'
            >
              {formik.isSubmitting ? (
                <div className='spinner-border spinner-border-sm me-2'></div>
              ) : (
                <i className='fas fa-save fa-lg fa-fw me-2'></i>
              )}
              {t('Add')}
            </button>
            <button
              type='button'
              className='btn btn-lg  btn-default mb-1 w-100 rounded-1'
              onClick={onClose}
            >
              <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
            </button>
          </div>
        </div>
      </Form>
    </>
  );
};

export default AddFaCategory;
